/**
 * Store domain from a Shopify callback — ferried out of the URL at boot.
 *
 * The BE redirects to `/integrations?shopify=<outcome>&shop=<domain>`. The
 * integrations tab compares that domain with the store the brand now holds, but
 * the domain is merchant data. Left in the URL it reaches Sentry and Clarity,
 * browser history, and the protected-route guard's `/login?redirect=…` before
 * the tab ever mounts (issue #727).
 *
 * So it leaves the URL synchronously at boot, the way captureClaimToken handles
 * the claim token. `?shopify=` stays: it is a fixed outcome value, not merchant
 * data, and the post-login return path relies on it.
 *
 * sessionStorage, not the `storage` helper: a callback is one tab, one sitting,
 * and must survive the login detour, which ends in a full page load.
 */
const KEY = 'discovr-brand-shopify-return-shop';
const CALLBACK_PATH = '/integrations';
const OUTCOME_PARAM = 'shopify';
const SHOP_PARAM = 'shop';

const isBrowser = (): boolean => typeof window !== 'undefined';

/** Survives the page but not a reload, for when storage is blocked. */
let memoryShop: string | undefined;
function isShopifyCallback(params: URLSearchParams): boolean {
  return window.location.pathname === CALLBACK_PATH && params.has(OUTCOME_PARAM);
}

/** False when storage is blocked: the value only lives until the next reload. */
function storeShop(shop: string): boolean {
  try {
    window.sessionStorage.setItem(KEY, shop);
    return true;
  } catch {
    memoryShop = shop;
    return false;
  }
}

function stripShopParam(params: URLSearchParams): void {
  params.delete(SHOP_PARAM);
  const search = params.toString();
  const query = search ? `?${search}` : '';
  try {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + query + window.location.hash,
    );
  } catch {
    // Nothing left to try; never break boot over this.
  }
}

/**
 * Move `?shop=` out of a Shopify callback URL into sessionStorage. Call once at
 * app boot, after captureClaimToken and BEFORE the router runs.
 */
export function captureShopifyReturnShop(): void {
  if (!isBrowser()) return;
  const params = new URLSearchParams(window.location.search);
  const shop = params.get(SHOP_PARAM);
  if (!shop) return;
  if (!isShopifyCallback(params)) return;

  const stored = storeShop(shop);
  // Blocked storage: leave ?shop= for the login redirect to carry, as before;
  // the integrations tab reads it and strips it from the URL.
  if (!stored) return;
  stripShopParam(params);
}

export function readShopifyReturnShop(): string | undefined {
  if (!isBrowser()) return memoryShop;
  try {
    return window.sessionStorage.getItem(KEY) ?? memoryShop;
  } catch {
    return memoryShop;
  }
}

/** Single-use: a stale domain would be compared against a later, unrelated callback. */
export function clearShopifyReturnShop(): void {
  memoryShop = undefined;
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Storage is blocked, so there is nothing stored to remove.
  }
}
